import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Card, Col, notification, Row, Space, Typography } from 'antd';
import { useTrackingCode } from "react-hubspot-tracking-code-hook";

import { useShared } from '../context/shared.context';
import { CompanyInformationForm } from './company-information';
import { useLeadService } from '../context/lead-service.context';
import { stepRequestMapperWithoutEmail } from '../utils/mapper';

import { SummaryInformationForm } from './summary-information';
import { useNavigate } from "react-router-dom";
import { useIntegrations } from "../context/integrations.context";
import { useResponsive } from "../utils/use-responsive";
import { FormPage } from "./contents/form-page";

export type Step = {
  key: string;
  component: any;
};

export const SignUpSteps: React.VFC = () => {

  const [values, setValues] = useState<any>(() => {
    if (localStorage.getItem('lowFunding')) {
      const values = localStorage.getItem('pre-registration-form');
      try {
        return JSON.parse(values || '');
      } catch {
        return {};
      }
    } else {
      return {};
    }
  });

  const { setPathPageView } = useTrackingCode();
  setPathPageView("/");

  const [data, setData] = useState<any>(null);
  const [customerCreated, setCustomerCreated] = useState<boolean>(false);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(() => {
    if (values?.summary && values?.company && values?.customers) return 2;
    if (values?.summary && values?.company) return 1;
    return 0;
  });

  const navigate = useNavigate();
  const { isMobile, isXs } = useResponsive();
  const { searchExperian } = useIntegrations();
  const { generateCustomer, updateCustomer, generateCompany, generateLead, setLoading, loading } = useLeadService();
  const [experianError, setExperianError] = useState<boolean>(false);


  const [steps] = useState<Step[]>([
    {
      key: 'summary',
      component: SummaryInformationForm,
    },
    {
      key: 'company',
      component: CompanyInformationForm,
    }
  ]);

  const {
    listCountries,
    listCurrencies,
    listJobTitles,
    listAccountancyPackage
  } = useShared();

  const identityProperties = {
    'hs_object_source_label': 'signup-form',
    'phone': '',
    'firstname': '',
    'lastname': '',
    'companynames': '',
    'company_registration_number': ''
  }

  useEffect(() => {
    if (localStorage.getItem('lowFunding')) {
      localStorage.removeItem('lowFunding');
    } else {
      localStorage.clear();
      setActiveStepIndex(0);
    }
    listCountries.execute();
    listCurrencies.execute();
    listJobTitles.execute();
    listAccountancyPackage.execute();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (loading) {
      window?.scrollTo?.(0, 0);

      let emailModified = customerCreated && data?.email !== values?.summary?.email;

      const newValues = saveLocalStorage(data);

      if (steps?.length !== activeStepIndex + 1) {   
      let payload = getPayload(newValues, emailModified);
      checkCompanyCountry(null, payload);
      } else {                                                // Second step
        if (newValues.company.fundingRequired < 50000) {
          setLoading(false);
          navigate('/low-funding');
          identityProperties.hs_object_source_label = 'signup-form-low-funding';
          identityProperties.firstname = newValues?.summary?.firstname;
          identityProperties.lastname = newValues?.summary?.surname;
          identityProperties.phone = newValues?.summary?.phone;
          identityProperties.companynames = newValues?.company?.name;
          identityProperties.company_registration_number = newValues?.company?.registrationNumber;
          // setIdentity(newValues?.summary?.email, identityProperties);
          // Hubspot Enterprise required for TrackEvent
          // setTrackEvent({eventId: "submit-form-low-funding", value: newValues});
        } else {
          submitCompany(newValues);
        }
      }
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeStep: Step = useMemo<Step>(() => {
    return steps?.[activeStepIndex];
  }, [steps, activeStepIndex]);

  function saveLocalStorage( data: any ) {
    const newValues = {
      ...(values || {}),
      [activeStep.key]: data,
    };
    localStorage.setItem(
      'pre-registration-form',
      JSON.stringify(newValues)
    );
    setValues(newValues);
    return newValues;
  }

  function showError() {
    notification.error({
      message: 'An error occurred',
      description: 'Please try again. If the problem persists, contact the site administrator',
      placement: 'top'
    });
  }

  function callToExperian( data: any, payload: any ) {
    searchExperian.execute({
      countryCode: payload?.countryOfIncorporation,
      companyRegisteredName: payload?.companyName,
    }).then(() => {
      console.log("searchExperian res", searchExperian);
      setExperianError(false);
    }).catch(( err ) => {
      setExperianError(true);
    }).finally(() => {
      setLoading(false);
      setActiveStepIndex(activeStepIndex + 1);
    });
  }

  function getPayload( newValues: any, emailModified: boolean ) {
    let payload = newValues.summary;
    if (!emailModified && customerCreated) {
      payload = stepRequestMapperWithoutEmail(newValues);
    }
    return payload;
  }

  function checkCompanyCountry( data: any, payload: any ) {
    if (payload.countryOfIncorporation !== "Other") {
      callToExperian(data, payload);
    } else {
      setActiveStepIndex(activeStepIndex + 1);
      setLoading(false);
      window?.scrollTo?.(0, 0);
    }
  }

  function submitCompany( newValues: any ) {
  const newData = {
    "email": newValues.summary.email,
    "firstname": newValues.summary.firstname,
    "surname": newValues.summary.surname,
    "phone": newValues.summary.phone,
    "jobTitle": newValues.summary.jobTitle,
    "companyName": newValues.company.name,
    "registrationNumber": newValues.company.registrationNumber,
    "currency": newValues.company.currency,
    "fundingRequired": newValues.company.fundingRequired,
    "country": newValues.company.country,
    "businessModel": newValues.company.businessModel,
    "turnover": newValues.company.turnover,
    "forecastTurnover": newValues.company.forecastTurnover,
    "comments": newValues.company.comments,
    "hubSpotTracking": {
      "hutk": Cookies.get('hubspotutk'),
      "pageUri": window.location.href,
      "pageName": "SignUp Form"
    }
  }
  console.log('Payload being sent to generateLead:', newData);
    let customerId = localStorage.getItem('customerId');
    // newValues.company.hubSpotTracking = {};
    // newValues.company.hubSpotTracking.hutk = Cookies.get('hubspotutk');
    // newValues.company.hubSpotTracking.pageName = 'SignUp Form';
    // newValues.company.hubSpotTracking.pageUri = window.location.href;
    generateLead.execute(newData).then(( response ) => {
      localStorage.clear();
      localStorage.setItem('companyId', response.id);
      identityProperties.firstname = newValues?.summary?.firstname;
      identityProperties.lastname = newValues?.summary?.surname;
      identityProperties.phone = newValues?.summary?.phone;
      identityProperties.companynames = newValues?.company?.name;
      identityProperties.company_registration_number = newValues?.company?.registrationNumber;
      // setIdentity(newValues?.summary?.email, identityProperties);
      // Hubspot Enterprise required for TrackEvent
     //  setTrackEvent({eventId: "submit-form", value: newValues});
      navigate('/connect-open-banking');
    }).catch(() => {
        showError();
    }).finally(() => {
        setLoading(false);
    });
  }

  return (
    activeStep && (
      <FormPage displayFooter={true}>
        <Row justify="center" style={{ width: '100%' }}>
          <Col span={20}>
            <Space direction={'vertical'} size={'large'} className={'full-width'}>
              <Card bordered={!isMobile} bodyStyle={isXs ? { padding: '0' } : { padding: '2rem' }}>
                <Typography.Title
                  type={'secondary'}
                  style={{ margin: 0 }}
                  level={5}
                  data-cy="app-page-step-count-title"
                >
                  Step {activeStepIndex + 1} of {steps?.length}
                </Typography.Title>

                {React.createElement(activeStep.component, {
                  key: activeStep.key,
                  defaultValues: values?.[activeStep.key],
                  onNext: async ( data: any ) => {
                    setLoading(true);
                    setData(data);
                  },
                  onBack: ( data: any ) => {
                    setCustomerCreated(true);
                    // delete data['companyName'];
                    // delete data['registrationNumber'];
                    saveLocalStorage(data);
                    if (activeStepIndex > 0) {
                      setActiveStepIndex(activeStepIndex - 1);
                    }
                  },
                  businessModel:
                    activeStepIndex === 2 ? values?.company?.businessModel : undefined,
                  hasBack: activeStepIndex > 0,
                  experianResult: experianError ? null : searchExperian.result?.items,
                  openCompany: (searchExperian.result?.items?.length > 0 && values?.summary?.countryOfIncorporation !== 'Other') ? true : false,
                })}
              </Card>
            </Space>
          </Col>
        </Row>
      </FormPage>
    )
  );
};
